import { useSyncExternalStore } from 'react';

/**
 * Sessao do front. O JWT fica em memoria (variavel) e espelhado em sessionStorage
 * para sobreviver a reload da aba. A empresa escolhida vai no header X-Empresa-Id.
 */
const STORAGE_KEY = 'triade.sessao';

export type EscopoSessao = 'APP' | 'TROCA_SENHA';

interface Sessao {
  accessToken: string | null;
  empresaId: string | null;
  /** TROCA_SENHA = primeiro login com senha temporaria: so a tela de troca de senha funciona (a API nega o resto). */
  escopo: EscopoSessao;
}

let sessao: Sessao = lerStorage();
const ouvintes = new Set<() => void>();

function lerStorage(): Sessao {
  try {
    const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { accessToken: null, empresaId: null, escopo: 'APP' };
    const v = JSON.parse(raw) as Partial<Sessao>;
    return { accessToken: v.accessToken ?? null, empresaId: v.empresaId ?? null, escopo: v.escopo === 'TROCA_SENHA' ? 'TROCA_SENHA' : 'APP' };
  } catch {
    return { accessToken: null, empresaId: null, escopo: 'APP' };
  }
}

function gravar(next: Sessao): void {
  sessao = next;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage indisponivel (modo privado restrito): segue so em memoria.
  }
  ouvintes.forEach((fn) => fn());
}

export function getToken(): string | null {
  return sessao.accessToken;
}

export function getEmpresaId(): string | null {
  return sessao.empresaId;
}

export function getEscopo(): EscopoSessao {
  return sessao.escopo;
}

/** Guarda o token. Trocar de token (login, troca de senha) mantem a empresa escolhida quando `manterEmpresa`. */
export function iniciarSessao(accessToken: string, escopo: EscopoSessao = 'APP', manterEmpresa = false): void {
  gravar({ accessToken, empresaId: manterEmpresa ? sessao.empresaId : null, escopo });
}

export function escolherEmpresa(empresaId: string | null): void {
  gravar({ ...sessao, empresaId });
}

export function encerrarSessao(): void {
  gravar({ accessToken: null, empresaId: null, escopo: 'APP' });
}

function assinar(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

/** Estado reativo da sessao para componentes React. */
export function useSessao(): Sessao {
  return useSyncExternalStore(assinar, () => sessao, () => sessao);
}

/** Handler chamado quando a API responde 401 (o router registra o redirect para /login). */
let aoExpirar: (() => void) | null = null;
export function registrarAoExpirar(fn: () => void): void {
  aoExpirar = fn;
}
export function notificarExpiracao(): void {
  encerrarSessao();
  aoExpirar?.();
}
